/**
 * Whether an MJPG service should be swapped for its active JPG sibling.
 *
 * - iOS WebKit cannot render `multipart/x-mixed-replace` in an `<img>`.
 * - A signed-in native-app WebView has no cookie, only a bearer token, and an
 *   `<img>` cannot carry it: an MJPG stream is refused in an IP_RESTRICTED space
 *   for an owner outside the range (403, MU, 7. 10. 2026) and in a members-only
 *   room for its own members (401, MU, 2. 11. 2026). The JPG path fetches frames
 *   with the bearer, so prefer it there.
 *
 * Browsers and anonymous app users keep MJPG.
 */
export function shouldPreferJpgSibling(input) {
    if (input.format !== 'MJPG')
        return false;
    return input.isIOSWebKit || input.isNativeWithToken;
}
export function resolveMjpgRendering(input) {
    if (input.isAdHoc || !shouldPreferJpgSibling(input))
        return 'stream';
    if (input.hasActiveJpgSibling)
        return 'sibling';
    return input.streamFailedButFetchOk ? 'frame-poll' : 'stream';
}
/**
 * Could a failed stream of this service turn into frame polling? Only for a viewer
 * {@link resolveMjpgRendering} would move, on a persisted MJPG service.
 *
 * `serviceFormat` is undefined while the selector switches to a service whose info
 * is not loaded yet; the viewer then still shows the previous format, so that one
 * decides. Reading only the service's format left the answer false for a stream
 * that failed in that window, and the switch never came (review of #857).
 */
export function canStreamFallBackToFramePoll(input) {
    if (input.viewerFormat !== 'MJPG' || !input.hasSourceId || input.isAdHoc)
        return false;
    return shouldPreferJpgSibling({
        isIOSWebKit: input.isIOSWebKit,
        isNativeWithToken: input.isNativeWithToken,
        format: input.serviceFormat ?? input.viewerFormat,
    });
}
export function streamProbeAction(input) {
    if (input.outcome === 'refused')
        return 'refused';
    if (!input.mayFallBackToFramePoll)
        return 'none';
    return input.outcome === 'ok' && input.hasSourceId ? 'frame-poll' : 'generic';
}
//# sourceMappingURL=jpg-sibling.js.map